/**
 * Editing an event funnel's capture form.
 *
 * The form was hard-coded. Renaming "Investor type" to "Position", turning
 * "Investment range" into a free-text facility name, changing the interest
 * options to dental / podiatry / optometry, and dropping "How did you meet the
 * team" were four content changes that each needed a developer and a deploy.
 * The person who wants them is the person running the conference.
 *
 * The columns behind the fields are NOT renamed, and this is deliberate enough
 * to say on the screen: `investor_type` keeps its name while displaying
 * "Position", because renaming it would orphan every lead captured before the
 * change. The label is presentation; the column is storage.
 */
import React, { useEffect, useState } from 'react';
import { X, Plus, GripVertical } from 'lucide-react';
import { Button } from '../../components/new-components/ui/button';
import { Input } from '../../components/new-components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/new-components/ui/dialog';
import { TENANT } from '../../config';
import { PRACTICE_OPTIONS } from '../../config/practices';

export interface FunnelField {
  key: string;
  label: string;
  type: 'text' | 'textarea' | 'select';
  enabled: boolean;
  required: boolean;
  options: string[];
}

/** Mirrors FIELD_KEYS in backend/src/services/eventFunnelForm.js. */
const FIELD_KEYS: { key: string; hint: string }[] = [
  { key: 'investor_type', hint: 'stored as investor_type' },
  { key: 'investment_range', hint: 'stored as investment_range' },
  { key: 'primary_interest', hint: 'stored as primary_interest' },
  { key: 'how_met', hint: 'stored as how_met' },
];

/** The landing page's own photography, offered rather than typed from memory. */
const STOCK: { path: string; label: string }[] = [
  { path: '/emerge/2173110193.jpg', label: 'Senior living environment' },
  { path: '/emerge/1468437321.jpg', label: 'Care professional' },
  { path: '/emerge/qtq80-j0YVBE-1024x683.jpeg', label: 'Dentistry' },
  { path: '/emerge/2181979363-1024x683.jpg', label: 'Podiatry' },
  { path: '/emerge/2151920926-3.jpg', label: 'Clinician with resident' },
  { path: '/emerge/qtq80-v6CYhL-1024x683.jpeg', label: 'Audiology' },
];

/**
 * The form a brand-new funnel opens with. Mirrors defaultFields() on the
 * server, so what the editor previews for an unconfigured funnel is what the
 * public page renders for it.
 *
 * A tenant that sells investments starts with its capital-markets fields. A
 * tenant that does not starts with a job title and a facility name — the
 * column names are unchanged underneath, and the hint beside each field says
 * so.
 */
const DEFAULTS: FunnelField[] = TENANT.features.investing
  ? [
    { key: 'investor_type', label: 'Investor type', type: 'select', enabled: true, required: false, options: [] },
    { key: 'investment_range', label: 'Investment range', type: 'select', enabled: true, required: false, options: [] },
    { key: 'primary_interest', label: 'Primary interest', type: 'select', enabled: true, required: false, options: [...PRACTICE_OPTIONS] },
    { key: 'how_met', label: 'How did you meet the team?', type: 'textarea', enabled: true, required: false, options: [] },
  ]
  : [
    { key: 'investor_type', label: 'Position', type: 'text', enabled: true, required: false, options: [] },
    { key: 'investment_range', label: 'Facility / company name', type: 'text', enabled: true, required: false, options: [] },
    { key: 'primary_interest', label: 'Primary interest', type: 'select', enabled: true, required: false, options: [...PRACTICE_OPTIONS] },
    { key: 'how_met', label: 'How did you meet the team?', type: 'textarea', enabled: false, required: false, options: [] },
  ];

interface Props {
  open: boolean;
  onClose: () => void;
  conferenceId: string | null;
  initialFields: FunnelField[] | null;
  initialHero: string | null;
  saving: boolean;
  onSave: (fields: FunnelField[], heroImageUrl: string | null) => void;
}

export default function EventFunnelFormEditor({
  open, onClose, conferenceId, initialFields, initialHero, saving, onSave,
}: Props) {
  const [fields, setFields] = useState<FunnelField[]>(DEFAULTS);
  const [hero, setHero] = useState('');

  useEffect(() => {
    if (!open) return;
    // Merge onto the canonical key list, so a field the funnel has never
    // configured still appears and can be turned on.
    const byKey = new Map((initialFields || []).map((f) => [f.key, f]));
    setFields(DEFAULTS.map((d) => ({ ...d, ...(byKey.get(d.key) || {}) })));
    setHero(initialHero || '');
  }, [open, initialFields, initialHero]);

  const patch = (key: string, change: Partial<FunnelField>) =>
    setFields((prev) => prev.map((f) => (f.key === key ? { ...f, ...change } : f)));

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit the capture form</DialogTitle>
        </DialogHeader>

        {conferenceId && (
          <div className="rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-600">
            Conference ID <span className="font-mono font-semibold text-gray-900">{conferenceId}</span>
            {' '}— fixed for the life of this funnel, and stamped on every lead it captures.
          </div>
        )}

        <div className="space-y-4">
          {fields.map((f) => {
            const hint = FIELD_KEYS.find((k) => k.key === f.key)?.hint;
            return (
              <div key={f.key} className="rounded-lg border border-gray-200 p-3">
                <div className="flex flex-wrap items-center gap-3">
                  <GripVertical className="h-4 w-4 shrink-0 text-gray-300" />
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={f.enabled}
                      onChange={(e) => patch(f.key, { enabled: e.target.checked })}
                      className="rounded border-gray-300"
                    />
                    Show
                  </label>
                  <Input
                    value={f.label}
                    onChange={(e) => patch(f.key, { label: e.target.value })}
                    placeholder="Label shown to the visitor"
                    className="h-9 flex-1 min-w-[200px] text-sm"
                  />
                  <select
                    value={f.type}
                    onChange={(e) => {
                      const type = e.target.value as FunnelField['type'];
                      // Choosing Dropdown shows the choices it will actually
                      // get. The server fills an empty one with the practices
                      // either way; putting them on screen means the editor
                      // never previews something other than what visitors see,
                      // and they can be overwritten right here.
                      patch(f.key, type === 'select' && f.options.length === 0
                        ? { type, options: [...PRACTICE_OPTIONS] }
                        : { type });
                    }}
                    className="h-9 rounded-md border border-gray-300 bg-white px-2 text-sm"
                  >
                    <option value="text">Text</option>
                    <option value="textarea">Paragraph</option>
                    <option value="select">Dropdown</option>
                  </select>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={f.required}
                      onChange={(e) => patch(f.key, { required: e.target.checked })}
                      className="rounded border-gray-300"
                    />
                    Required
                  </label>
                </div>

                {f.type === 'select' && (
                  <div className="mt-3 pl-7">
                    <p className="mb-1 text-xs text-gray-500">
                      Choices, one per line
                      {f.options.length === 0 && (
                        <span className="text-gray-400">
                          {' '}— left empty, this offers the practices:{' '}
                          {PRACTICE_OPTIONS.join(', ')}.
                        </span>
                      )}
                    </p>
                    <textarea
                      rows={Math.max(3, f.options.length + 1)}
                      value={f.options.join('\n')}
                      onChange={(e) => patch(f.key, {
                        options: e.target.value.split('\n').map((o) => o.trim()).filter(Boolean),
                      })}
                      placeholder={PRACTICE_OPTIONS.join('\n')}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    />
                  </div>
                )}

                <p className="mt-2 pl-7 text-xs text-gray-400">
                  {hint} — the column keeps its name so leads captured before a rename stay readable.
                </p>
              </div>
            );
          })}
        </div>

        <div className="mt-2">
          <p className="mb-1 text-sm font-medium text-gray-800">Background image</p>
          <p className="mb-2 text-xs text-gray-500">
            Left empty, the page uses {TENANT.branding.name}&apos;s own image.
          </p>
          <Input
            value={hero}
            onChange={(e) => setHero(e.target.value)}
            placeholder={TENANT.branding.eventHeroPath}
            className="h-9 text-sm"
          />
          <div className="mt-2 flex flex-wrap gap-2">
            {STOCK.map((img) => (
              <button
                key={img.path}
                type="button"
                onClick={() => setHero(img.path)}
                className={`overflow-hidden rounded border text-left ${hero === img.path ? 'border-[#242424] ring-2 ring-[#242424]/20' : 'border-gray-200'}`}
                title={img.label}
              >
                <img src={img.path} alt={img.label} className="h-14 w-24 object-cover" />
              </button>
            ))}
            {hero && (
              <button
                type="button"
                onClick={() => setHero('')}
                className="flex h-14 w-24 items-center justify-center rounded border border-dashed border-gray-300 text-xs text-gray-500"
              >
                <X className="mr-1 h-3 w-3" /> Clear
              </button>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={() => onSave(fields, hero.trim() || null)} disabled={saving}>
            {saving ? 'Saving…' : 'Save form'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export { DEFAULTS as DEFAULT_FUNNEL_FIELDS, STOCK as EVENT_STOCK_IMAGES, Plus };
